import React, { useEffect, useState } from 'react';
import moment from 'moment';
import AccountFilterTable from 'views/common/AccountFilterTable';
import { AccountCell, LinkCell, TextCell } from 'components/Cells/Cells';
import { translate } from 'components/Translation';
import { exportToXLS } from 'utils/export/export';
import { AccountsService } from 'services/accounts/accounts';
import { BillsService } from 'services/bills/bills';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import { useDispatch } from 'react-redux';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { searchItemsInColumns } from 'utils/searchTable/searchTable';
import { BillingAccountsService } from 'services/billingAccounts/billingAccounts';
import { setToast } from 'redux/modules/toast/actions';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import AddBillingAccountDialog from './subcomponents/AddBillingAccountDialog';
import Add from '@material-ui/icons/Add';
import { throwError } from 'rxjs';
import DeleteOTP, { DeleteValues } from './subcomponents/DeleteBillingAccount/DeleteOTP';
import CannotDelete from './subcomponents/DeleteBillingAccount/CannotDelete';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';

interface IBillingAccount extends SingleBillId {
	account?: SingleCorpAccount;
}

interface ISelectedAccount {
	name: string;
	id: string | null;
}

type Dialog = 'DeleteOTP' | 'Add' | 'CannotDelete' | null;

const BillingAccountsList = () => {
	const [loading, setLoading] = useState({ fetch: true, delete: false, accountDetails: false });
	const [billingAccounts, setBillingAccounts] = useState<Array<IBillingAccount>>([]);
	const [searchValue, setSearchValue] = useState('');
	const [documentId, setDocumentId] = useState<string>('');
	const [dialog, setDialog] = useState<Dialog>(null);
	const [selected, setSelected] = useState([]);
	const [accounts, setAccounts] = useState<Array<SingleCorpAccount>>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [filteredData, setFilteredData] = useState<Array<IBillingAccount>>([]);
	const dispatch = useDispatch();

	const [deleteValues, setDeleteValues] = useState<DeleteValues>({
		contactNumber: '',
		otpError: false,
		accountToDelete: null,
	});
	const deleteAccountActions = (() => {
		const clean = () => {
			setDialog(null);
			setSelected([]);
			setDeleteValues({ contactNumber: '', otpError: false, accountToDelete: null });
		};

		return {
			deleteAccount: (token: string) => {
				setLoading((loading) => ({ ...loading, delete: true }));
				BillingAccountsService.deleteAllAccountsForArborIdOperation(
					deleteValues.accountToDelete!.billingId!,
					deleteValues.accountToDelete!.accountId!,
					token,
					documentId
				)
					.pipe(finalize(() => setLoading((loading) => ({ ...loading, delete: false }))))
					.subscribe(
						({ response }) => {
							if (response?.NullResult?.length === 0) {
								setFilteredData((filteredData) =>
									filteredData.filter(
										(account: IBillingAccount) =>
											account.billingId !== deleteValues.accountToDelete!.billingId!
									)
								);
								dispatch(
									setToast({
										error: false,
										actionType: translate('deleted'),
										label: translate('payments.billingAccounts.delete.success'),
									})
								);
								clean();
							}
						},
						(error) => {
							if (error.response.code === 'ERROR_VALIDATING_OTP') {
								setDeleteValues((deleteValues) => ({ ...deleteValues, otpError: true }));
							} else {
								throwError(error);
							}
						}
					);
			},
			sendOTP: () => {
				const account = filteredData[selected[0]];
				setDeleteValues((deleteValues) => ({ ...deleteValues, accountToDelete: account }));

				setLoading((loading) => ({ ...loading, accountDetails: true }));
				AccountsService.getCorpAccountDetails(account.accountId)
					.pipe(finalize(() => setLoading((loading) => ({ ...loading, accountDetails: false }))))
					.subscribe(({ accountDetails: { contactNumber, documentId } }) => {
						if (!!contactNumber) {
							setDocumentId(documentId);
							setLoading((loading) => ({ ...loading, delete: true }));
							BillingAccountsService.deleteAllAccountsForArborIdInitialize(
								account.billingId,
								account.accountId
							)
								.pipe(finalize(() => setLoading((loading) => ({ ...loading, delete: false }))))
								.subscribe(
									({ response }) => {
										setDialog('DeleteOTP');
										setDeleteValues((deleteValues) => ({
											...deleteValues,
											contactNumber:
												response?.deleteAllAccountsForArborIdInitializeResponse?.contactNumber,
										}));
									},
									() => clean()
								);
						} else {
							setDialog('CannotDelete');
						}
					});
			},
			clean,
		};
	})();

	useEffect(() => {
		if (Object.values(loading).some(Boolean)) {
			dispatch(showLoadingIndicator());
		} else {
			dispatch(hideLoadingIndicator());
		}
	}, [loading, dispatch]);

	useEffect(() => {
		AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => setAccounts(accounts));
	}, []);

	useEffect(() => setSelected([]), [billingAccounts]);

	useEffect(() => {
		if (accounts && accounts.length > 0) {
			setLoading((loading) => ({ ...loading, fetch: true }));
			dispatch(showLoadingIndicator());
			BillsService.getCorpBillingIds()
				.pipe(
					finalize(() => {
						dispatch(hideLoadingIndicator());
						setLoading((loading) => ({ ...loading, fetch: false }));
					})
				)
				.subscribe((response) => {
					setBillingAccounts(
						response.billingIds.map((billingId) => ({
							...billingId,
							account: accounts.find((account) => account.accountId === billingId.accountId),
						}))
					);
				});
		}
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch, accounts]);

	useEffect(() => {
		setSelected([]);
		let data = [...billingAccounts];
		if (selectedAccount && selectedAccount.id !== null) {
			data = data.filter(({ account }) => !!account && account.accountId === selectedAccount.id);
		}

		if (searchValue !== '') {
			data = searchItemsInColumns(data, ['billingAccountName', 'billingId'], searchValue);
		}
		setFilteredData(data);
	}, [searchValue, selectedAccount, billingAccounts]);

	const exportXLS = React.useMemo(
		() => () => {
			const source = selected.length > 0 ? selected.map((index) => filteredData[index]) : filteredData;
			const data = source.map((account) => ({
				'Billing account name': account.billingAccountName,
				'Billing account number': account.billingId,
				'Organization name': account?.account?.accountName,
				'Lines amount': account.lines,
			}));
			exportToXLS(data, `billing accounts ${moment().format('LLL')}}`);
		},
		[filteredData, selected]
	);

	useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: 'My Active Services',
				language: getLanguage(),
			},
		});
	}, []);

	return (
		<div style={{ width: '100%' }}>
			<ConditionalRender
				show={dialog === 'DeleteOTP'}
				onTrue={() => (
					<DeleteOTP
						handleCancel={deleteAccountActions.clean}
						values={deleteValues}
						handleDelete={deleteAccountActions.deleteAccount}
					/>
				)}
			/>
			<AccountFilterTable
				accounts={accounts}
				setAccount={setSelectedAccount}
				selectedAccount={selectedAccount}
				filterHeader={translate('payments.billingAccounts.filterHeader')}
				filters={[]}
				exportToXLS={exportXLS}
				filtersValue={{}}
				onFilterChange={() => {}}
				data={filteredData}
				selected={selected}
				onSelect={setSelected}
				selectPanel={{
					selectedElements: selected.length,
					confirmationDialogTitle: translate('payments.billingAccounts.deleteHeader'),
					confirmationDialogBody: translate(
						'payments.billingAccounts.delete.areYouSure',
						filteredData[selected[0]]?.billingId || '',
						filteredData[selected[0]]?.billingAccountName || ''
					),
					downloadMenuItems: [
						{
							name: 'XLSX',
							onClick: exportXLS,
						},
					],
					disableDeleteText: translate('payments.billingAccounts.deleteManyTooltip'),
					disableDelete: selected.length !== 1,
					deleteItemsAction: 'active-services:delete-billing-account',
					// A user could try to remove other accounts while the requests are being processed
					// to avoid this, we disable deleting when there is a request to delete anything
					deleteItems: !deleteValues.accountToDelete ? deleteAccountActions.sendOTP : undefined,
				}}
				buttons={{
					text: (
						<>
							<Add />
							{translate('active.services.add.billing.account')}
						</>
					),
					onClick: () => setDialog('Add'),
					buttonProps: {
						withIcon: true,
					},
					action: 'active-services:add-billing-account',
				}}
				setSearchInputValue={setSearchValue}
				searchInputValue={searchValue}
				loading={loading.fetch}
				columns={[
					{
						Header: translate('payments.billingAccounts.billingAccountName'),
						Cell: ({ original }: any) => (
							<LinkCell
								path={`/dashboard/active-services/${original.billingId}/${original.account.accountId}`}
								text={original.billingAccountName}
							/>
						),
						accessor: 'billingAccountName',
					},
					{
						Header: translate('payments.billingAccounts.billingAccountNumber'),
						Cell: ({ original }: any) => <TextCell text={original.billingId} />,
						accessor: 'billingId',
					},
					{
						Header: translate('payments.billingAccounts.organizationName'),
						Cell: ({ original }: any) => <AccountCell text={original.account.accountName} />,
						accessor: 'account.accountName',
					},
					{
						Header: translate('payments.billingAccounts.linesAmount'),
						Cell: ({ original }: any) => <TextCell text={original.lines} />,
						accessor: 'lines',
					},
				]}
			/>
			{/* TODO: remove these any */}
			<AddBillingAccountDialog
				isOpen={dialog === 'Add'}
				closeDialog={() => setDialog(null)}
				accounts={accounts}
			/>
			<ConditionalRender
				show={dialog === 'CannotDelete'}
				onTrue={() => <CannotDelete hideDialog={() => deleteAccountActions.clean()} />}
			/>
		</div>
	);
};

export default BillingAccountsList;
